import { ApplyProductData } from '@/api/apply-product/types';
import { EquitySelectedEnum, PricingTypeEnum } from '@/enums/rights';
import store from '@/store';
import { CouponTypeEnum, CouponRolePo } from '@/api/coupon/types';
import { RightPackageTypeEnum } from '@/api/rights/types';
import ProductCommodity from '@/shared/commodity';
import { ubtModuleExposeNew, ubtSendError } from '@/shared/utils/ubt';
import { couponTypePriorityList } from './product';
import { StateType } from './types';

/** 计算当前渠道可提现的最大金额 */
export const getMaxLoanAmount = (product: ApplyProductData, isLimitAmount: boolean): number => {
  const { everyMaxAmount, applyAmountStep } = product?.productInfo || {};
  const { availableAmount, limitAvailableAmount } = product?.userCredit || {};

  // 根据提现步长、可提现金额、不包含临时额度的可提现金额、单笔最大提现金额，计算出用户当前最大可提现金额
  if ((everyMaxAmount && typeof Number(everyMaxAmount) === 'number')
    && (applyAmountStep && typeof Number(applyAmountStep) === 'number')
    && (availableAmount && typeof Number(availableAmount) === 'number')) {
    return Math.floor(
      Math.min(
        isLimitAmount ? limitAvailableAmount : availableAmount,
        everyMaxAmount,
      ) / applyAmountStep,
    ) * applyAmountStep;
  }
  return 0;
};


const pricingTypeMap = new Map<string, PricingTypeEnum | undefined>();
export const updatePricingTypeMap = (
  productId?: string,
  pricingType?: PricingTypeEnum,
):void => {
  if (productId) {
    pricingTypeMap.set(productId, pricingType);
  }
};

export const getPricingTypeByProductId = (
  productId?: string,
): PricingTypeEnum | undefined => {
  if (productId) {
    return pricingTypeMap.get(productId);
  }
  return undefined;
};

/** 是否勾选了权益 */
export const getEquitySelected = (
  params: {
    productId:string,
    equitySelected?: EquitySelectedEnum,
    /** 是否已经初始化完权益相关数据，主要是是否能购买权益，这部分数据会在每次初始化渠道时清空*/
    hasInitRightStatus?: boolean | null,
  },
): EquitySelectedEnum | undefined => {
  const { productId, equitySelected, hasInitRightStatus } = params;
  // 其他情况根据用户定价标签返回页面状态
  const pricingType = getPricingTypeByProductId(productId);
  // 当渠道的权益状态还没返回时，调用试算接口不给equitySelected赋值
  // bugfix: https://metis2.hellobike.cn/team/detail/issue?filterId=3846&openDetail=611996&teamId=154
  if (!hasInitRightStatus) {
    return undefined;
  }
  // 如果指定用户是否选在极速则根据用户选择状态调用试算
  if (equitySelected !== undefined) {
    return equitySelected;
  }

  if (pricingType) {
    return store.state.loanRight.showSpeed ? EquitySelectedEnum.YES : EquitySelectedEnum.NO;
  }
  // 不返回值，则默认不传值
  return undefined;
};

/** 获取默认选中的券 */
export const getDefaultSelectedCoupon = (couponList?: CouponRolePo[]): CouponRolePo | undefined => {
  /** 当前默认选中的券以及优先级 */
  let curDefaultSelectedCoupon: CouponRolePo | undefined;
  let curCouponPriority = 0;

  if (!couponList) {
    return curDefaultSelectedCoupon;
  }

  /** 券类型优先级映射 */
  const couponTypePriorityMap: Partial<Record<CouponTypeEnum, number>> = {};
  couponTypePriorityList.forEach((couponType, index) => {
    couponTypePriorityMap[couponType] = index + 1;
  });

  /** 遍历获取最高优先级券 */
  couponList.some((coupon) => {
    if (coupon.availability) {
      const priority = couponTypePriorityMap[coupon.freeCouponType as CouponTypeEnum] || 0;
      // 如果当前券类型优先级大于当前最高优先级，则更新当前最高优先级券
      if (priority > curCouponPriority) {
        curCouponPriority = priority;
        curDefaultSelectedCoupon = coupon;
      }
      // 如果当前券类型优先级为最高优先级，则直接返回当前券
      if (priority === couponTypePriorityList.length) {
        return true;
      }
    }
    return false;
  });

  return curDefaultSelectedCoupon;
};


/** 更新点击的productId值 */
export const updateClickedSubmitProductIds = (productId: string): void => {
  try {
    const { autrakyZhenxiangCardState } = store.state.applyProduct;

    if (!autrakyZhenxiangCardState?.clickedSubmitProductIds) {
      return;
    }

    const existingIds = autrakyZhenxiangCardState?.clickedSubmitProductIds;
    if (!(existingIds.filter((item) => item === productId).length)) {
      existingIds.push(productId);
    }

    const needBuyCard = autrakyZhenxiangCardState?.autrakyNeedBuyZhenxiangCard;
    const updatedState: StateType['autrakyZhenxiangCardState'] = {
      clickedSubmitProductIds: existingIds,
      autrakyNeedBuyZhenxiangCard: needBuyCard || false,
    };

    store.dispatch(
      'applyProduct/$updateState',
      { autrakyZhenxiangCardState: updatedState } as Partial<StateType>,
    );
  } catch (error) {
    ubtSendError(new Error('updateClickedSubmitProductIds error'));
  }
};

/** 如果当前产品未点击提交按钮，且需要勾选权益，上报事件
 * 详见需求：https://metis2.hellobike.cn/team/detail/issue?filterId=3844&openDetail=647807&teamId=198
 * 后续如果不需要该事件上报后可删除
 */
export const dispatchNotBuyZhenxiangCardEvent = (params: {
  pageId: string,
  clickedSubmitProductIds: string[],
  packageType?: RightPackageTypeEnum,
  currentProductId?: string,
  autrakyNeedBuyZhenxiangCard?: boolean,
}):void => {
  try {
    const {
      clickedSubmitProductIds,
      pageId,
      autrakyNeedBuyZhenxiangCard,
    } = params;
    const hasClickedAutarkyProduct = clickedSubmitProductIds?.filter(
    (item) => item === ProductCommodity.AUTARKY_B_COMMODITY,
  ).length;
    if (!hasClickedAutarkyProduct && autrakyNeedBuyZhenxiangCard) {
      ubtModuleExposeNew(pageId, 'finance_loan_applyProduct_notBuyZhenxiangCardExit', {
        ...params,
        clickedProductIds: Array.from(clickedSubmitProductIds).join(','),
      });
    }
  } catch (e) {
    ubtSendError(new Error('dispatchNotBuyZhenxiangCardEvent error'));
  }
};
